import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import React from 'react';
import { SysproJob } from 'screens/JobPaste/types';
import classes from './importSysproJobs.module.scss';
import { useImportSysproJobs } from './useImportSysproJobs';

const ImportSysproJobsModal: React.FC = () => {
  const {
    visibility,
    onClose,
    isLoading,
    sysproJobs,
    selectedJobs,
    setSelectedJobs,
    handleImportJobs,
    jobErrors,
    hasValidationError
  } = useImportSysproJobs();

  // Helper function to determine button label based on state
  const getButtonLabel = (): string => {
    if (isLoading) {
      return 'Importing...';
    }

    if (hasValidationError) {
      return 'Retry Import';
    }

    return 'Import Selected Jobs';
  };

  const renderFooter = () => {
    return (
      <div className={classes.footer}>
        <Button
          label="Cancel"
          className={classes.cancelButton}
          onClick={onClose}
          disabled={isLoading}
        />
        <Button
          label={getButtonLabel()}
          className={classes.importButton}
          onClick={handleImportJobs}
          loading={isLoading}
          disabled={selectedJobs.length === 0 || isLoading}
        />
      </div>
    );
  };

  const renderHeader = () => {
    return (
      <div className={classes.modalHeader}>
        <h2>Import Syspro Jobs</h2>
        <div className={classes.selectionCount}>
          {selectedJobs.length > 0 && !isLoading && (
            <span>
              {selectedJobs.length} job{selectedJobs.length !== 1 ? 's' : ''} selected
            </span>
          )}
          {hasValidationError && jobErrors.size > 0 && (
            <span className={classes.errorCount}>
              {jobErrors.size} job{jobErrors.size !== 1 ? 's' : ''} with errors
            </span>
          )}
        </div>
      </div>
    );
  };

  // Custom body template for error column
  const errorBodyTemplate = (rowData: SysproJob) => {
    const error = jobErrors.get(rowData.job_id);
    if (error) {
      return (
        <div className={classes.errorMessage} title={error}>
          {error}
        </div>
      );
    }
    return null;
  };

  // Custom row class to highlight error rows
  const rowClassName = (data: SysproJob) => {
    return jobErrors.has(data.job_id) ? classes.errorRow : '';
  };

  return (
    <Dialog
      header={renderHeader()}
      visible={visibility}
      blockScroll
      resizable={false}
      draggable={false}
      onHide={onClose}
      modal
      style={{ width: '95vw', maxWidth: '1800px', height: '90vh' }}
      className={classes.importSysproJobsModal}
      footer={renderFooter()}
      closable={!isLoading}
    >
      <div className={classes.importSysproJobsContainer}>
        {isLoading ? (
          <div className={classes.loaderContainer}>
            <ProgressSpinner />
            <p>{sysproJobs.length ? 'Importing jobs...' : 'Loading Syspro Jobs...'}</p>
          </div>
        ) : (
          <DataTable
            value={sysproJobs}
            scrollable
            scrollHeight="calc(80vh - 120px)"
            selectionMode="multiple"
            selection={selectedJobs}
            onSelectionChange={e => setSelectedJobs(e.value)}
            dataKey="job_id"
            className={classes.jobsTable}
            emptyMessage="No jobs available"
            resizableColumns
            columnResizeMode="fit"
            rowClassName={rowClassName}
          >
            <Column
              selectionMode="multiple"
              headerStyle={{ width: '3rem' }}
              className={classes.selectionColumn}
              frozen={true}
              alignFrozen="left"
            />
            <Column field="job_id" header="Job ID" sortable style={{ minWidth: '120px' }} frozen={true} alignFrozen="left" />
            <Column field="priority" header="Prty." sortable style={{ minWidth: '50px' }} />
            <Column field="room" header="Room" sortable style={{ minWidth: '50px' }} />
            <Column field="ndc" header="NDC" sortable style={{ minWidth: '150px' }} />
            <Column
              field="description"
              header="Description"
              sortable
              style={{ minWidth: '300px' }}
            />
            <Column
              field="qty_of_containers_to_fill"
              header="Containers to Fill"
              sortable
              style={{ minWidth: '150px' }}
            />
            <Column
              field="qty_per_container"
              header="Qty Per Container"
              sortable
              style={{ minWidth: '150px' }}
            />
            <Column
              field="qty_units_to_package"
              header="Units to Package"
              sortable
              style={{ minWidth: '150px' }}
            />
            <Column field="run_type" header="Run Type" sortable style={{ minWidth: '120px' }} />
            <Column
              field="confirmed_date"
              header="Confirmed Date"
              sortable
              style={{ minWidth: '150px' }}
            />
            <Column field="status" header="Status" sortable style={{ minWidth: '100px' }} />
            {hasValidationError && (
              <Column
                header="Validation Errors"
                body={errorBodyTemplate}
                style={{ minWidth: '300px' }}
                className={classes.errorColumn}
              />
            )}
          </DataTable>
        )}
      </div>
    </Dialog>
  );
};

export default observer(ImportSysproJobsModal);
